import { useEffect, useState } from "react";
import { Handshake, ChevronDown, ChevronUp } from "lucide-react";
import api from "@/lib/api";

function statusBadge(status) {
  return status === "paying"
    ? "border-accent text-accent"
    : status === "trialing"
      ? "border-white/30 text-white/70"
      : "border-white/20 text-white/50";
}

export default function AdminAffiliates() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/admin/affiliates").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const t = data.totals;
  const tiles = [
    [t.affiliates, "Houses sharing"],
    [t.clicks, "Link clicks"],
    [t.signups, "Referred members"],
    [t.paying, "Paying now"],
    [`$${(t.earned_cents / 100).toFixed(2)}`, "Commissions owed"],
  ];

  return (
    <section className="mb-16" data-testid="admin-affiliates">
      <p className="overline-label mb-4 flex items-center gap-2">
        <Handshake size={14} className="text-accent" /> Affiliate network — every referral, who brought whom
      </p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border border-white/10 mb-8" data-testid="admin-affiliate-totals">
        {tiles.map(([v, l]) => (
          <div key={l} className="bg-background p-6">
            <p className="font-display text-3xl mb-1">{v}</p>
            <p className="overline-label !text-[10px]">{l}</p>
          </div>
        ))}
      </div>

      {data.affiliates.length === 0 ? (
        <p className="font-display italic text-2xl text-white/40">No houses are sharing links yet.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {data.affiliates.map((a) => (
            <div key={a.id} className="panel p-6" data-testid={`admin-affiliate-${a.id}`}>
              <button
                className="w-full flex flex-wrap items-center gap-x-6 gap-y-2 text-left"
                onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}
                data-testid={`admin-affiliate-toggle-${a.id}`}
              >
                <div className="min-w-[200px]">
                  <p className="font-display text-2xl">{a.name}</p>
                  <p className="font-grotesk text-[10px] uppercase tracking-[0.15em] text-white/40">{a.email}</p>
                </div>
                <code className="font-grotesk text-[11px] text-white/60 border border-white/15 px-3 py-1.5 truncate max-w-[260px]" data-testid={`admin-affiliate-link-${a.id}`}>
                  {a.url}
                </code>
                <p className="font-grotesk text-[11px] uppercase tracking-[0.15em] text-white/60">
                  {a.clicks} clicks · {a.signups} joined · {a.paying} paying
                </p>
                <p className="font-grotesk text-sm text-accent ml-auto">${(a.earned_cents / 100).toFixed(2)}</p>
                {open[a.id] ? <ChevronUp size={16} className="text-white/50" /> : <ChevronDown size={16} className="text-white/50" />}
              </button>

              {open[a.id] && (
                <div className="mt-6 border-t border-white/10 pt-4" data-testid={`admin-affiliate-referrals-${a.id}`}>
                  {a.referrals.length === 0 ? (
                    <p className="text-sm text-white/40 italic">Clicks so far, nobody joined yet.</p>
                  ) : (
                    <div className="flex flex-col divide-y divide-white/10">
                      {a.referrals.map((r) => (
                        <div key={r.id} className="py-3 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid={`admin-referral-row-${r.id}`}>
                          <p className="text-sm text-white/85 min-w-[160px]">{r.name}</p>
                          <p className="text-xs text-white/40 flex-1">{r.email}</p>
                          <p className="font-grotesk text-[10px] uppercase tracking-[0.12em] text-white/50">
                            {r.role === "model" ? "Talent" : "Brand"} · joined {r.signed_up ? new Date(r.signed_up).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                          </p>
                          <span className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 ${statusBadge(r.status)}`} data-testid={`admin-referral-status-${r.id}`}>
                            {r.status}
                          </span>
                          <p className="font-grotesk text-xs text-white/70 w-20 text-right">${(r.earned_cents / 100).toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
